import { AGENT_COPY } from '../language/agent-copy';

/**
 * Does this model prose merely restate the confirmation card?
 *
 * On WhatsApp the card is its own interactive bubble, so any prose the
 * model wrote in the same turn rides a preamble bubble *before* it. That
 * is right when the prose answers something else («السعر 1,600 ريال»)
 * and wrong when the model typed the card body itself — the user then
 * sees the same «👤 *مطلوب تأكيد* — تعيين السائق…» twice, once as text
 * and once with Yes/No buttons (production, 16 Sep 2026, conv
 * 6aa6056d…: the model echoed the assign card verbatim minus its
 * footer). The tool result already tells the model not to; this is the
 * code-level guarantee for when it does anyway.
 *
 * Two independent tests, either is enough:
 *
 * 1. The prose carries a card header / footer marker («مطلوب تأكيد»,
 *    «جاهز للتأكيد», «أرسل *تم*», "Confirmation needed", …). Only the
 *    card is allowed to say these.
 * 2. Nearly every token of the prose already appears in the card
 *    (≥ {@link ECHO_TOKEN_OVERLAP} of ≥ 4 tokens). Pure text-set
 *    containment — an echo that drops the footer or re-orders lines
 *    still counts; a real answer with its own words does not.
 *
 * Pure and channel-agnostic; lives here so the WhatsApp channel and the
 * admin transcript flattener agree.
 */
const CARD_MARKERS =
  /مطلوب تأكيد|جاهز للتأكيد|جاهزة للتأكيد|أرسل \*?تم\*?|Confirmation needed|Ready to confirm|Reply \*?yes\*?|Confirmation chahiye|haan\*? bhejein/i;

/** Share of prose tokens that must already be on the card to call it an echo. */
const ECHO_TOKEN_OVERLAP = 0.8;
/** Below this many tokens a high overlap is coincidence («تمام.»), not an echo. */
const ECHO_MIN_TOKENS = 4;

/** Markdown, emoji, bidi marks, punctuation and digits-as-refs all go; words stay. */
const normalizeTokens = (text: string): string[] =>
  text
    .replace(/\p{Extended_Pictographic}|\u{FE0F}|\u{200D}|[\u{1F3FB}-\u{1F3FF}]/gu, ' ')
    .replace(/[\u200E\u200F\u202A-\u202E\u2066-\u2069\u061C]/g, '')
    .replace(/[*_~`>#|]/g, ' ')
    .replace(/[.,،؛;:!?؟"'«»“”()[\]{}—–-]/g, ' ')
    .toLowerCase()
    .split(/\s+/)
    .map(token => token.trim())
    .filter(token => token.length > 1);

/**
 * Prose that points back at a staged card («وثيقة النقل جاهزة للتأكيد…
 * أكّد عشان نصدرها», «Ready to confirm — reply yes»). Such a bubble is
 * not a new question: a bare «تم» after it answers the card behind it.
 * Used by the pending-card lookup so a model restatement between the
 * card and the confirm does not hide the card (runtime, 25 Sep 2026:
 * «؟» → model restated the card in words → «تم» found no card → a
 * second card was staged and confirmed twice).
 */
export const restatesStagedCard = (prose: string): boolean =>
  CARD_MARKERS.test(prose) ||
  // «أكّد عشان…», «أكّد بـ تم أو ألغِ», «تم أو ألغِ», «رد بـ تم» — the
  // card's own footer said in the model's words (runtime, 25 Sep 2026:
  // «تم تجهيز تعديل الحمولة … — أكّد بـ تم أو ألغِ» rode a preamble
  // bubble above the identical edit card).
  /أكّد|أكد\s+(عشان|حتى|لـ?إصدار|بـ?\s*\*?تم)|(?<![\p{L}])تم\*?\s+أو\s+أل[غـ]|رد\s+بـ?\s*\*?تم|للتأكيد|to confirm|confirm\s+karein|tasdeeq/iu.test(
    prose,
  );

const escapeRegExp = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const CARD_HEADER_KEYS = ['ready_header', 'waybill_ready_header', 'assign_header'] as const;

/**
 * Each card header's fixed words in every language pack («جاهز للإصدار»,
 * "Ready to issue", …), as the start of a line — a header, not a word
 * that happens to appear in a sentence.
 */
const CARD_HEADER_LINE = new RegExp(
  `(?:^|\\n)[^\\p{L}\\n]*(?:${Object.values(AGENT_COPY)
    .flatMap(tree => {
      const confirmation = tree.confirmation;
      if (!confirmation || typeof confirmation === 'string') return [];
      return CARD_HEADER_KEYS.map(key => confirmation[key]);
    })
    .filter((header): header is string => typeof header === 'string')
    .map(header => header.split(/\{\{|—/u)[0].replace(/[^\p{L}\p{M}\s]/gu, '').trim())
    .filter(Boolean)
    .map(escapeRegExp)
    .join('|')})`,
  'u',
);

/**
 * Prose shaped like a confirmation card — its header line or its footer
 * marker. Only a staged card may look like this: typed by the model, it
 * reads as something to confirm while nothing is staged behind it
 * (production, 21 Oct 2026, conv 6ad880d1…: a follow-up turn with no tool
 * call copied the «📄 *جاهز للإصدار*» card from history).
 */
export const looksLikeConfirmationCard = (prose: string): boolean =>
  CARD_MARKERS.test(prose) || CARD_HEADER_LINE.test(prose);

export const isConfirmationEcho = (prose: string, cardText: string): boolean => {
  const text = prose.trim();
  if (!text || !cardText.trim()) return false;
  if (CARD_MARKERS.test(text)) return true;

  const proseTokens = normalizeTokens(text);
  if (proseTokens.length < ECHO_MIN_TOKENS) return false;
  const cardTokens = new Set(normalizeTokens(cardText));
  const overlapping = proseTokens.filter(token => cardTokens.has(token)).length;
  return overlapping / proseTokens.length >= ECHO_TOKEN_OVERLAP;
};
